import React, { FC, useCallback, useMemo, useState } from 'react';
import PaginatedReduxTableWithHeader from '../../common/PaginatedReduxTableWithHeader';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button } from 'antd';
import { EmptyState } from '../../empty_state/EmptyState';
import {
	troubleshootingGuidesRestCrudThunksForBuyer,
	troubleshootingGuidesRestCrudThunksForSupplier,
} from '../../../thunks/troubleshooting_guides_thunks';
import TSLocationsDisplay from './TSLocationsDisplay';
import TSProblemTypesDisplay from './TSProblemTypesDisplay';
import TSPrioritiesDisplay from './TSPrioritiesDisplay';
import { TROUBLESHOOTING_GUIDES_CRUD_ACTION_CREATORS } from '../../../actions/troubleshooting_guides_actions';
import { nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { getLinkWIthBackLinkParams } from '../../../utils/HistoryUtils';
import RegionsLocationsTreeSelect from '../../regions_tree_select/RegionsLocationsTreeSelect';
import { FILTER_CONFIG_NAMES, ROLE_TYPES } from '../../../utils/DataConstants';
import ProblemTypeTreeSelect from '../../problem_type_tree_select';
import { withUserType } from '../../withUserType';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesHeirarchicalForSupplier,
} from '../../../thunks/problem_types_thunks';
import {
	workOrderPrioritiesRestCrudThunksForBuyer,
	workOrderPrioritiesRestCrudThunksForSupplier,
} from '../../../thunks/work_order_priorities_thunks';
import TSSorters from './TSSorters';
import WorkOrderCreateButton from '../../common/WorkOrderCreateButton';
import TroubleshootingGuideNewOrEditModal from '../TroubleshootingGuideNewOrEditModal';
import AccessPermissionChecker from '../../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../../utils/AuthUtils';

const TC_NAME = 'troubleshootingGuidesIndex';
const TS_ADVANCED_FILTERS = 'troubleshooting_advanced_filters';

const TroubleshootingOverviewAllPage: FC<any> = ({
	updateFilters,
	troubleshootingGuides,
	clearAndUpdateFilters,
	history,
	location,
	locations,
	problemTypes,
	workOrderPriorities,
	fetchProblemTypes,
	fetchWorkOrderPriorities,
	fetchMultipleWorkOrderPriorities,
	updateSorters,
	userType,
}): React.ReactElement => {
	const [newGuideVisible, setNewGuideVisible] = useState(false);

	const onRow = useCallback(
		(record) => ({
			onClick: () =>
				history.push(
					getLinkWIthBackLinkParams(
						location,
						'Back to guides',
						`/${userType}/workflows/troubleshooting/detail/${record.id}`
					)
				),
		}),
		[history, location, userType]
	);

	const onNewGuideSuccess = useCallback(
		(guide) => {
			history.push(`/${userType}/workflows/troubleshooting/detail/${guide.id}`);
		},
		[history, userType]
	);

	const _hideNewGuide = () => setNewGuideVisible(false);
	const _showNewGuide = () => setNewGuideVisible(true);

	const createNewButton = (
		<AccessPermissionChecker name={PERMISSION_NAMES.ADD_TROUBLESHSHOOTING}>
			<Button style={{ marginLeft: 16 }} type="primary" size="large" onClick={_showNewGuide}>
				New Guide
			</Button>
		</AccessPermissionChecker>
	);

	const isAssociationsNotSpecified = (record) =>
		nullSafeGetOrElse('problemTypeIds', record, []).length === 0 &&
		nullSafeGetOrElse('locationIds', record, []).length === 0 &&
		nullSafeGetOrElse('workOrderPriorityIds', record, []).length === 0;

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				render: (name, record) => {
					const stepCount = nullSafeGetOrElse('troubleshootingSteps', record, []).length;
					return (
						<div className="flex flex-col">
							<div>{name || '--'}</div>
							{record.description ? (
								<div className="text-gray-400">{record.description}</div>
							) : null}
							<div className="text-gray-400">{`${stepCount} step${
								stepCount !== 1 ? 's' : ''
							}`}</div>
						</div>
					);
				},
			},
			{
				title: 'Problem Types',
				dataIndex: 'problemTypes',
				render: (problemTypes, record) => (
					<TSProblemTypesDisplay
						problemTypes={problemTypes}
						notSpecified={isAssociationsNotSpecified(record)}
					/>
				),
			},
			{
				title: 'Locations',
				dataIndex: 'locations',
				render: (locations, record) => (
					<TSLocationsDisplay
						locations={locations}
						notSpecified={isAssociationsNotSpecified(record)}
					/>
				),
			},
			{
				title: 'Priorities',
				dataIndex: 'workOrderPriorities',
				render: (workOrderPriorities, record) => (
					<TSPrioritiesDisplay
						priorities={workOrderPriorities}
						notSpecified={isAssociationsNotSpecified(record)}
					/>
				),
			},
		],
		[]
	);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Location',
				fieldName: FILTER_CONFIG_NAMES.LOCATION,
				mode: 'multiple',
				elementDefined: true,
				stateSlice: locations,
				targetCollectionName: TS_ADVANCED_FILTERS,
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: RegionsLocationsTreeSelect,
			},
			{
				label: 'Problem Type',
				fieldName: FILTER_CONFIG_NAMES.PROBLEM_TYPE,
				mode: 'multiple',
				stateSlice: problemTypes,
				targetCollectionName: TS_ADVANCED_FILTERS,
				fetch: fetchProblemTypes,
				fetchMultiple: () => {},
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: ProblemTypeTreeSelect,
			},
			{
				label: 'Priority',
				fieldName: FILTER_CONFIG_NAMES.PRIORITY,
				mode: 'multiple',
				stateSlice: workOrderPriorities,
				targetCollectionName: TS_ADVANCED_FILTERS,
				fetch: fetchWorkOrderPriorities,
				fetchMultiple: fetchMultipleWorkOrderPriorities,
				sortBy: { sort_by: 'name', order: 'ascend' },
			},
		],
		[
			fetchMultipleWorkOrderPriorities,
			fetchProblemTypes,
			fetchWorkOrderPriorities,
			locations,
			problemTypes,
			workOrderPriorities,
		]
	);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={TC_NAME}
				updateFilters={updateFilters}
				stateSlice={troubleshootingGuides}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				sorters={TSSorters}
				updateSorters={updateSorters}
				entityCollectionName="troubleshooting_guides"
				tableColumns={columns}
				onTableRow={onRow}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? troubleshootingGuidesRestCrudThunksForSupplier.read
						: troubleshootingGuidesRestCrudThunksForBuyer.read
				}
				rightActions={
					<div className="flex flex-row items-center gap-2">
						<WorkOrderCreateButton
							align="center"
							size="large"
							type="ghost"
							backText={'Back to troubleshooting guides'}
							title="Preview"
							getLink={(location, BACK_TEXT, path) => {
								const params = {
									onlyForPreview: true,
								};
								const paramsString = new URLSearchParams(params).toString();

								return getLinkWIthBackLinkParams(location, BACK_TEXT, `${path}?${paramsString}`);
							}}
						/>
						{createNewButton}
					</div>
				}
				showHeader
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="No Data"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
							/>
						}
						headline={'No troubleshooting found'}
						body={<div style={{ textAlign: 'center' }}>{createNewButton}</div>}
					/>
				}
			/>
			{newGuideVisible ? (
				<TroubleshootingGuideNewOrEditModal
					onSuccess={onNewGuideSuccess}
					onCancel={_hideNewGuide}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	troubleshootingGuides: state.troubleshooting_guides,
	locations: state.locations,
	problemTypes: state.problem_types,
	workOrderPriorities: state.work_order_priorities,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(TROUBLESHOOTING_GUIDES_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(
			TROUBLESHOOTING_GUIDES_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			TROUBLESHOOTING_GUIDES_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesHeirarchicalForSupplier(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: problemTypesHeirarchicalForBuyer(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchWorkOrderPriorities: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrderPrioritiesRestCrudThunksForSupplier
				: workOrderPrioritiesRestCrudThunksForBuyer;
		return dispatch(
			fnToCall.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchMultipleWorkOrderPriorities: (ids, targetCollectionName) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrderPrioritiesRestCrudThunksForSupplier
				: workOrderPrioritiesRestCrudThunksForBuyer;
		return dispatch(fnToCall.readMultiple(ids, targetCollectionName));
	},
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(TroubleshootingOverviewAllPage))
);
